/* component-reviews.js — Slice 38 · P4 (+ P5 client truncation).
 * Loaded by blocks/product-reviews.liquid + sections/reviews.liquid when a review modal, a see-more
 * cap, or a manual word limit is active (B13 — no block-level <script>). Three responsibilities:
 *   1. delegated template-swap handler for the review modal (swaps the clicked card's full review
 *      into the shell's slot; the title is a static storefront string);
 *   2. see-more (drops [data-reviews-cap] to reveal every capped review);
 *   3. client-side truncation of MANUAL review cards under a [data-words] host — closing the S20
 *      child word-limit limitation: a card with no server read-more is truncated to N words with a
 *      read-more affordance byte-matching review-card's, driving the same swap handler.
 *
 * Why a swap: snippets/review-card.liquid emits, per truncated card, a trigger
 * <button class="kv-review__more" data-popup-open="<cid>"> and a sibling
 * <template data-review-full="<cid>"> holding the full review. The shared popup engine opens a
 * dialog by matching data-popup-open against a dialog's data-popup-trigger-id and NEVER swaps
 * content, so one shell + this swap is required to host variable content across N cards
 * (slice-38 §7.3 check 1). The shell carries no trigger-id, so neither popup-engine.js nor
 * component-loader.js intercepts these cards — this handler is their sole driver.
 *
 * Capture phase so the swap always precedes any bubble-phase popup listener. The engine is
 * lazy: if it is not yet loaded we warm the shared `popup` surface via the sanctioned
 * window.theme.loader.warm API (component-loader.js), then dispatch popup:open. (component-loader
 * also idle-loads the engine on presence of the [data-popup-dialog], so most clicks find it ready.)
 *
 * Rules: J1/J12 (addEventListener, IIFE-dedup) · J5 (CustomEvent) · J8 (uses the documented
 * window.theme.loader API, no new global) · C2.
 */
(function () {
  'use strict';
  if (window.__BLOCKDEDUP_REVIEWS__) return;
  window.__BLOCKDEDUP_REVIEWS__ = true;

  function openShell(id) {
    if (window.__POPUP_ENGINE__) {
      document.dispatchEvent(new CustomEvent('popup:open', { detail: { id: id } }));
    } else if (window.theme && window.theme.loader && window.theme.loader.warm) {
      window.theme.loader.warm('popup', function () {
        document.dispatchEvent(new CustomEvent('popup:open', { detail: { id: id } }));
      });
    }
  }

  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var btn = t.closest('.kv-review__more[data-popup-open]');
    if (!btn) return;
    var surface = btn.closest('[data-reviews-surface]');
    if (!surface) return;
    var dlg = surface.querySelector('[data-popup-dialog]');
    var slot = dlg && dlg.querySelector('[data-review-modal-slot]');
    var cid = btn.getAttribute('data-popup-open');
    var esc = (window.CSS && CSS.escape) ? CSS.escape(cid) : cid;
    var tpl = surface.querySelector('template[data-review-full="' + esc + '"]');
    if (!dlg || !slot || !tpl) return;
    event.preventDefault();
    // The modal title is a static storefront string ("Customer review") set in the markup; the swap
    // handler only injects the review body. The author name lives on the card inside the modal only.
    slot.replaceChildren(tpl.content.cloneNode(true));
    openShell(dlg.getAttribute('data-popup-id'));
  }, true);

  // See-more (static + prv_scroll_cap). The list is capped in CSS via [data-reviews-cap]
  // + :nth-of-type (server-side per-item hiding is impossible for the content_for manual
  // source — S20); one click drops the cap attribute (revealing every review) and retires
  // the button. Delegated, capture phase.
  document.addEventListener('click', function (event) {
    var t = event.target;
    if (!t || !t.closest) return;
    var more = t.closest('[data-reviews-more]');
    if (!more) return;
    var surface = more.closest('[data-reviews-surface]');
    if (!surface) return;
    surface.removeAttribute('data-reviews-cap');
    more.hidden = true;
  }, true);

  // ---- Manual-card client truncation (closes the S20 child word-limit limitation) ----
  // A [data-words] host (the surface) holds cards; any card WITHOUT a server-rendered read-more
  // (the manual _review children) is truncated in the browser to N words, with a read-more
  // affordance byte-matching review-card's server output — so the swap handler above drives its
  // modal too. Server-truncated cards (metafield/store) already carry .kv-review__more and are left.
  function truncateManualCards(scope) {
    var root = (scope && scope.querySelectorAll) ? scope : document;
    var hosts = root.querySelectorAll('[data-words]');
    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      var limit = parseInt(host.getAttribute('data-words'), 10);
      if (!limit || limit < 1) continue;
      var label = host.getAttribute('data-read-more-label') || '';
      var hasModal = !!host.querySelector('[data-popup-dialog]');
      var cards = host.querySelectorAll('.kv-review');
      var idx = 0;
      for (var c = 0; c < cards.length; c++) {
        var card = cards[c];
        if (card.classList.contains('kv-review--modal')) continue; // the swapped-in full modal card
        if (card.hasAttribute('data-cw-done')) continue;           // already processed
        if (card.querySelector('.kv-review__more')) continue;      // server read-more -> leave it
        var bodyEl = card.querySelector('.kv-review__body');
        if (!bodyEl) continue;
        var text = (bodyEl.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) continue;
        var wordsArr = text.split(' ');
        card.setAttribute('data-cw-done', '');
        if (wordsArr.length <= limit) continue;
        var uid = 'cw-' + (idx++);
        if (hasModal) {
          var full = card.cloneNode(true);                         // full content BEFORE truncation
          full.removeAttribute('data-cw-done');
          full.classList.add('kv-review--modal');
          var tpl = document.createElement('template');
          tpl.setAttribute('data-review-full', uid);
          tpl.content.appendChild(full);
          host.appendChild(tpl);
        }
        bodyEl.textContent = wordsArr.slice(0, limit).join(' ') + '… ';
        var affordance;
        if (hasModal) {
          affordance = document.createElement('button');
          affordance.type = 'button';
          affordance.className = 'kv-review__more';
          affordance.setAttribute('data-popup-open', uid);
        } else {
          affordance = document.createElement('span');
          affordance.className = 'kv-review__more kv-review__more--static';
        }
        affordance.textContent = label;
        bodyEl.appendChild(affordance);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { truncateManualCards(document); });
  } else {
    truncateManualCards(document);
  }
  document.addEventListener('shopify:section:load', function (event) {
    truncateManualCards(event.target || document);
  });
})();
